import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DeliveryPricingRulesService } from 'src/generated_client/api/deliveryPricingRules.service';
import { CreateDeliveryPricingRuleDto, DeliveryPricingRuleDto } from 'src/generated_client/model/deliveryPricingRuleDto';
import { Category } from 'src/generated_client/model/category';
import { RuleType } from 'src/generated_client/model/ruleType';
import { DeliveryServiceType } from 'src/generated_client/model/deliveryServiceType';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-pricing-rules',
  templateUrl: './pricing-rules.component.html',
  styleUrls: ['./pricing-rules.component.css']
})
export class PricingRulesComponent implements OnInit, OnDestroy {
  pricingRules: DeliveryPricingRuleDto[] = [];
  editForm: FormGroup;
  isEditModalOpen = false;
  currentRule: DeliveryPricingRuleDto | null = null;
  private destroy$ = new Subject<void>();

  // Exponer los enums al template
  categories: Category[] = [];
  ruleTypes = Object.keys(RuleType).filter(k => isNaN(Number(k)));
  serviceOptions: { value: DeliveryServiceType | null, label: string }[] = [
    { value: null, label: 'General (todos los servicios)' },
    { value: DeliveryServiceType.Mensajeria, label: 'Mensajería' },
    { value: DeliveryServiceType.PuntoFijo, label: 'Punto Fijo' }
  ];

  constructor(
    private pricingRulesService: DeliveryPricingRulesService,
    private fb: FormBuilder
  ) {
        this.editForm = this.fb.group({
      value: [0, [Validators.required, Validators.min(0)]],
      service: [null]
    });
  }

  ngOnInit(): void {
    // Reinicializar el Subject si ya fue completado
    this.destroy$ = new Subject<void>();
    this.fetchPricingRules();
  }

  fetchPricingRules(): void {
    this.pricingRulesService.getAllDeliveryPricingRules()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (rules) => {
        console.log('Pricing rules data from service:', rules); // Debug log
        this.pricingRules = rules.sort((a, b) => a.category - b.category);
        this.categories = Object.values(Category).filter(value => typeof value === 'number') as Category[];
      },
      error: (error) => {
        console.error('Error fetching pricing rules:', error);
        // Opcional: mostrar un mensaje de error en la UI
      }
    });
  }

  onEdit(rule: DeliveryPricingRuleDto): void {
    this.currentRule = rule;
    this.editForm.patchValue({
      value: rule.value,
      service: rule.service ?? null
    });
    this.isEditModalOpen = true;
  }

  closeEditModal(): void {
    this.isEditModalOpen = false;
    this.currentRule = null;
    this.editForm.reset();
  }

  saveRule(): void {
    if (this.editForm.invalid || !this.currentRule) {
      return;
    }

    const formValue = this.editForm.value;
    const ruleToSave: CreateDeliveryPricingRuleDto = {
      category: this.currentRule.category,
      type: this.currentRule.type,
      service: formValue.service === null || formValue.service === '' ? null : Number(formValue.service),
      value: Number(formValue.value)
    };

    this.pricingRulesService.createDeliveryPricingRule(ruleToSave)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (success) => {
        if (success) {
          this.fetchPricingRules();
          this.closeEditModal();
          console.log('Regla de precio guardada con éxito');
        } else {
          console.error('Error al guardar la regla de precio: el backend devolvió false');
        }
      },
      error: (error) => {
        console.error('Error al guardar la regla de precio:', error);
      }
    });
  }

  // --- Métodos de Ayuda para el Template ---

  getIcon(category: Category): string {
    const icons: { [key in Category]?: string } = {
      [Category.TarifaBase]: '💵',
      [Category.DistanciaBase]: '📍',
      [Category.TarifaPorKmExtra]: '📍',
      [Category.DescuentoIdaVuelta]: '🔄',
      [Category.TarifaEsperaCada10Min]: '⏱️',
      [Category.CargoSobredimension]: '📦',
      [Category.RecargoLluviaBase]: '🌧️',
      [Category.RecargoLluviaPorKm]: '🌧️',
      [Category.ComisionPorPago]: '💳',
      [Category.FeriadoComun]: '🎉',
      [Category.FeriadoEspecial]: '🎊',
      [Category.RecargoFueraDeCiudad]: '🌍',
      [Category.IVA]: '🧾',

    };
    return icons[category] || '📋';
  }

  formatValue(rule: DeliveryPricingRuleDto): string {
    if (rule.value === null || rule.value === undefined) {
      return '';
    }
    switch (rule.type) {
      case RuleType.Fijo: return `$${rule.value.toFixed(2)}`;
      case RuleType.PorKm: return `$${rule.value.toFixed(2)}/km`;
      case RuleType.Porcentaje: return `${rule.value}%`;
      case RuleType.PorTiempo: return `$${rule.value.toFixed(2)}/10min`;
      default: return String(rule.value);
    }
  }

  getServiceLabel(service: DeliveryServiceType | null | undefined): string {
    if (service === null || service === undefined) {
      return 'General';
    }
    return service === DeliveryServiceType.Mensajeria ? 'Mensajería' : 'Punto Fijo';
  }

  getCategoryDescription(category: Category | string): string {
    switch (category) {
      case Category.TarifaBase: return 'Tarifa Base';
      case Category.DistanciaBase: return 'Distancia Base (km)';
      case Category.TarifaPorKmExtra: return 'Tarifa por Km Extra';
      case Category.DescuentoIdaVuelta: return 'Descuento Ida y Vuelta';
      case Category.TarifaEsperaCada10Min: return 'Tarifa de Espera (10 min)';
      case Category.CargoSobredimension: return 'Cargo por Sobredimensión';
      case Category.RecargoLluviaBase: return 'Recargo por Lluvia (Base)';
      case Category.RecargoLluviaPorKm: return 'Recargo por Lluvia (por Km)';
      case Category.ComisionPorPago: return 'Comisión por Pago';
      case Category.FeriadoComun: return 'Feriados al 50%';
      case Category.FeriadoEspecial: return 'Feriados al 100%';
      case Category.RecargoFueraDeCiudad: return 'Recargo Fuera de Ciudad';
      case Category.IVA: return 'IVA';
      default: return category as string;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Métodos de Ayuda para el Template ---

  getRuleTypeName(type: RuleType | string): string {
    // Convertir el valor numérico del enum a su clave de string (ej. 0 -> 'Fijo')
    const typeName = typeof type === 'number' ? RuleType[type] : type;

    const names: { [key: string]: string } = {
      Fijo: 'Monto Fijo',
      PorKm: 'Por Kilómetro',
      Porcentaje: 'Porcentaje',
      PorTiempo: 'Por Tiempo',
    };
    return names[typeName as string] || (typeName as string);
  }
}
